// src/pages/Inscricoes/SubscriptionModal.tsx
import React, { useEffect, useState, useCallback } from "react";
import api from "@/utils/connector/ApiConnector";
import { useUser } from "@/utils/userContext/UserContext";
import styles from "./SubscriptionModal.module.css";
import { Copy, X } from "lucide-react";
import { useGlobalModal } from "@/components/GlobalModalProvider/GlobalModalProvider";
import { useInvalidateCache } from "@/utils/hooks/useInvalidateCache";

export type Product = { id: string; name: string; [k: string]: any };
export type Customer = { id: string; name?: string; email?: string; [k: string]: any };
export type SubscriptionItem = {
  id: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  coworkingId?: string;
  product?: Product | null;
  customer?: Customer | null;
  [k: string]: any;
};

export default function SubscriptionModal({
  open,
  onClose,
  subscription,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  subscription: SubscriptionItem | null;
  onSaved?: (_updated: SubscriptionItem) => void;
}) {
  const userContext = useUser();
  const token = userContext?.userData?.auth;
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  const [copiedId, setCopiedId] = useState<string | null>(null);
  const modal= useGlobalModal();
  const { invalidateSubscriptions } = useInvalidateCache();

  const handleSave = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault?.();
    if (!editing) return;
    if (!subscription) return;
    if (!token) {
      modal.show({
        type: "error",
        title: "Sessão expirada",
        message: "Token ausente. Faça login novamente.",
        autoClose: 3500,
      });
      return;
    }
    try {
      setSaving(true);
      const body: any = {};
      if (startDate) body.startDate = new Date(startDate).toISOString(); // -> "2025-10-07T00:00:00.000Z"
      if (endDate)   body.endDate   = new Date(endDate).toISOString();
      if (status)    body.status    = status;

      Object.keys(body).forEach(k => (body[k] === null || body[k] === undefined) && delete body[k]);

      const resp: any = await api.patch(`/subscriptions/${subscription.id}`, body, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = (resp && (resp as any).data) ? (resp as any).data : resp;

      const updated: SubscriptionItem = {
        ...subscription,
        ...data,
        startDate: data.startDate ?? body.startDate ?? subscription.startDate,
        endDate: data.endDate ?? body.endDate ?? subscription.endDate,
        status: data.status ?? body.status ?? subscription.status,
      };

      onSaved && onSaved(updated);


      const successMessage = "Inscrição atualizada com sucesso!";
      modal.show({ type: "success", title: "Sucesso", message: successMessage, autoClose: 3000 });

      await invalidateSubscriptions();
      setEditing(false);
      onClose && onClose();
    } catch (err: any) {
      console.error("Erro ao salvar inscrição:", err);
      const message = err?.response?.data?.message ?? err?.message ?? "Erro ao salvar inscrição";
      modal.show({ type: "error", title: "Erro ao salvar", message, autoClose: 4500 });
    } finally {
      setSaving(false);
    }
  }, [editing, token, startDate, endDate, status, subscription, onSaved, onClose, modal, invalidateSubscriptions]);

// função de copiar (adicione também perto dos outros handlers)
  async function copyId(id?: string) {
    if (!id) return;
      try {
        if (typeof window !== "undefined" && window.navigator.clipboard?.writeText) {
          await window.navigator.clipboard.writeText(id);
        } else {
          const ta = document.createElement("textarea");
          ta.value = id;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.focus();
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        setCopiedId(id);
        window.setTimeout(() => setCopiedId(prev => (prev === id ? null : prev)), 2000);
      } catch (err) {
        console.error("Erro ao copiar id:", err);
      }
}

  useEffect(() => {
    if (!subscription) return;
    const sd = subscription.startDate ? new Date(subscription.startDate).toISOString().slice(0, 10) : "";
    const ed = subscription.endDate ? new Date(subscription.endDate).toISOString().slice(0, 10) : "";
    setStartDate(sd);
    setEndDate(ed);
    setStatus(subscription.status ?? "");
    setEditing(false);
  }, [subscription]);

  // lock body scroll quando modal aberto
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  // ESC para fechar
  useEffect(() => {
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === "Escape") onClose && onClose();
    };
    if (open) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open || !subscription) return null;

  const handleCancel = () => {
    const sd = subscription.startDate ? new Date(subscription.startDate).toISOString().slice(0, 10) : "";
    const ed = subscription.endDate ? new Date(subscription.endDate).toISOString().slice(0, 10) : "";
    setStartDate(sd);
    setEndDate(ed);
    setStatus(subscription.status ?? "");
    setEditing(false);
    onClose && onClose();
  };

  const renewSubscription = async () => {
    if (!token) {
      modal.show({
        type: "error",
        title: "Sessão expirada",
        message: "Token ausente. Faça login novamente.",
        autoClose: 3500,
      });
      return;
    }
    try {
      setSaving(true);
      const resp: any = await api.post(`/subscriptions/${subscription.id}/renew`, {}, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = (resp && (resp as any).data) ? (resp as any).data : resp;

      const newSubscription: SubscriptionItem = data;

      onSaved && onSaved(newSubscription);

      const successMessage = "Inscrição renovada com sucesso!";
      modal.show({ type: "success", title: "Sucesso", message: successMessage, autoClose: 3000 });

      await invalidateSubscriptions();
      setEditing(false);
      onClose && onClose();
    } catch (err: any) {
      console.error("Erro ao renovar inscrição:", err);
      const message = err?.response?.data?.message ?? err?.message ?? "Erro ao renovar inscrição";
      modal.show({ type: "error", title: "Erro ao renovar", message, autoClose: 4500 });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.modalOverlay} role="dialog" aria-modal="true">
      <div className={styles.modalContent}>
        <div className={styles.flexSeparator}>
          <h3 className={styles.modalTitle}>Inscrição</h3>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {!editing && (
              <button type="button" className={styles.submitButton} onClick={() => setEditing(true)}>
                Editar
              </button>
            )}
            <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Fechar modal">
              <X size={18} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {(subscription.id) ? (
            <div style={{ display: "flex", alignItems:'row' }}>
                <button
                  type="button"
                  aria-label={`Copiar código de inscrição ${subscription.id}`}
                  className={styles.copyButton}
                  onClick={() => copyId(subscription.id)}
                  title="Copiar código de inscrição"
                  style={{ padding: 6 }}
                >
                  <Copy size={14} />
                </button>
                <h4 style={{marginLeft:'10px'}}>Código da Inscrição</h4>
                {copiedId === subscription.id ? <small className={styles.copiedBadge}>Copiado!</small> : null}
                </div>

              ) : null}

          <div>
            <label style={{ fontWeight: 600, marginBottom: 6 }}>Cliente:</label>
            <div className={styles.sectionCard}>
              {subscription.customer?.name ?? subscription.customer?.email ?? "—"}<br />
              <small className={styles.sectionMeta}>{subscription.customer?.phone ?? ""} {subscription.customer?.email ? `• ${subscription.customer.email}` : ""}</small>
            </div>
          </div>

          <div>
            <label style={{ fontWeight: 600, marginBottom: 6 }}>Produto:</label>
            <div className={styles.sectionCard}>
              {subscription.product?.name ?? "—"}
            </div>
          </div>

          <div className={styles.formRow}>
            <label className={styles.formLabel}>
              Início:
              <input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                disabled={!editing}
                className="input-search"
              />
            </label>

            <label className={styles.formLabel}>
              Término:
              <input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                disabled={!editing}
                className="input-search"
              />
            </label>

            <label className={styles.statusLabel}>
              Status:
              <select value={status} onChange={e => setStatus(e.target.value)} disabled={!editing} className="input-search">
                <option value="">— Selecionar —</option>
                <option value="ACTIVE">Ativo</option>
                <option value="CANCELLED">Cancelado</option>
                <option value="EXPIRED">Expirado</option>
              </select>
            </label>

            <div className={styles.renewAction}>
                <button type="button" className={styles.renewButton} onClick={() => renewSubscription()}>
                  Renovar
                </button>
            </div>
          </div>

          {editing && (
            <div className={styles.modalActions}>
              <button type="button" className={styles.cancelButton} onClick={handleCancel} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className={styles.submitButton} disabled={saving}>
                {saving ? "Concluindo..." : "Concluir"}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
